import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {
  createAIReviewJob,
  approveProtocolIntelligence,
  createAIReviewJobGroup,
  createProtocolIntelligence,
  deleteAIReviewJobGroup,
  exportAIReviewJobs,
  deleteAIReviewJob,
  retryAIReviewJob,
  addJobToGroup,
  removeJobFromGroup,
  getAIReviewJobDetails,
  getAIReviewJobGroups,
  getAIReviewSourceType,
  getAllProtocolIntelligenceIds,
  getProtocolIntelligenceDetails,
  submitAIReviewJob,
  updateAIReviewJob,
  updateAIReviewJobGroup,
  getAIReviewJobResult,
  getAIReviewJobResultIssueCodeList,
  getAIReviewJobResultRelevancyList,
  updateAIReviewJobName,
  getAIReviewDocErrorPanelList,
  updateProtocolIntelligenceDetails,
  getJobList,
  getGroundTruthEvaluation,
  deleteAIProtocolIntelligence,
  getAIReviewJobDetailData,
} from '../../../api/endpoints/aiReviewApi';
import {
  IAIReviewJobGroupsResponse,
  ICreateAIReviewJobGroupPayload,
  ICreateAIReviewJobGroupResponse,
  IDeleteAIReviewJobGroupPayload,
  IUpdateAIReviewJobGroupPayload,
  IUpdateAIReviewJobGroupResponse,
  IAIReviewProtocolIntelligencePayload,
  IAIReviewProtocolIntelligenceResponse,
  IDeleteAIReviewJobGroupResponse,
  IGetAIReviewSourceTypeResponse,
  ICreateAIReviewJobResponse,
  ICreateAIReviewJobPayload,
  ISubmitAIReviewJobResponse,
  ISubmitAIReviewJobPayload,
  IGetAIReviewJobDetailsPayload,
  IGetAIReviewJobDetailsResponse,
  IUpdateAIReviewJobResponse,
  IUpdateAIReviewJobPayload,
  ProtocolIntelligenceApprovalResponse,
  IAIReviewJobsExportRequest,
  IExportAIReviewJobPdfPayload,
  IGetAIReviewJobResultPayload,
  IGetAIReviewJobResultResponse,
  IDeleteAIReviewJobPayload,
  IDeleteAIReviewJobResponse,
  IRetryAIReviewJobPayload,
  IRetryAIReviewJobResponse,
  IAddJobToGroupPayload,
  IAddJobToGroupResponse,
  IRemoveJobFromGroupPayload,
  IRemoveJobFromGroupResponse,
  IAIReviewJobResultIssueCodeListPayload,
  IAIReviewJobResultIssueCodeListResponse,
  IAIReviewJobResultRelevancyListPayload,
  IAIReviewJobResultRelevancyListResponse,
  IAIReviewEditJobNameResponse,
  IAIReviewEditJobNamePayload,
  IGetAIReviewDocErrorPanelPayload,
  IAIReviewDocErrorPanelResponse,
  IUpdateProtocolIntelligenceDetailsPayload,
  IUpdateProtocolIntelligenceDetailsResponse,
  IGetJobListResponse,
  IGetJobListPayload,
  IGetGroundTruthEvaluationResponse,
  IDeleteAIProtocolIntelligenceResponse,
  IDeleteAIProtocolIntelligencePayload,
} from '../../../api/types/aiReview.type';

export const REVIEW_JOB_GROUPS_QUERY_KEY = ['getAIReviewJobGroups'] as const;

export const PROTOCOL_INTELLIGENCE_QUERY_KEY = [
  'getProtocolIntelligence',
] as const;

export const SOURCE_TYPE_QUERY_KEY = ['getSourceType'] as const;

export const REVIEW_JOB_QUERY_KEY = ['getAIReviewJob'] as const;

export const REVIEW_DOC_ERROR_PANEL_QUERY_KEY = [
  'getAIReviewDocErrorPanel',
] as const;

export const useGetReviewJobGroups = () => {
  return useQuery<IAIReviewJobGroupsResponse, Error>({
    queryKey: [...REVIEW_JOB_GROUPS_QUERY_KEY],
    queryFn: () => getAIReviewJobGroups(),
  });
};

export const useCreateReviewJobGroup = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  const queryClient = useQueryClient();
  return useMutation<
    ICreateAIReviewJobGroupResponse,
    Error,
    ICreateAIReviewJobGroupPayload
  >({
    mutationFn: createAIReviewJobGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: REVIEW_JOB_GROUPS_QUERY_KEY});
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useUpdateReviewJobGroup = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  const queryClient = useQueryClient();
  return useMutation<
    IUpdateAIReviewJobGroupResponse,
    Error,
    IUpdateAIReviewJobGroupPayload
  >({
    mutationFn: updateAIReviewJobGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: REVIEW_JOB_GROUPS_QUERY_KEY});
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useDeleteReviewJobGroup = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  const queryClient = useQueryClient();
  return useMutation<
    IDeleteAIReviewJobGroupResponse,
    Error,
    IDeleteAIReviewJobGroupPayload
  >({
    mutationFn: deleteAIReviewJobGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: REVIEW_JOB_GROUPS_QUERY_KEY});
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetAllProtocolIntelligenceIds = (enabled: boolean = true) => {
  return useQuery<IAIReviewProtocolIntelligencePayload[], Error>({
    queryKey: [...PROTOCOL_INTELLIGENCE_QUERY_KEY],
    queryFn: () => getAllProtocolIntelligenceIds(),
    enabled,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false,
  });
};

export const useGetProtocolIntelligenceDetails = (protocolId?: string) => {
  return useQuery<IAIReviewProtocolIntelligenceResponse, Error>({
    queryKey: [...PROTOCOL_INTELLIGENCE_QUERY_KEY, protocolId],
    queryFn: () =>
      getProtocolIntelligenceDetails({
        protocolId: protocolId!,
      }),
    enabled: !!protocolId,
  });
};

export const useGetSourceType = () => {
  return useQuery<IGetAIReviewSourceTypeResponse, Error>({
    queryKey: [...SOURCE_TYPE_QUERY_KEY],
    queryFn: () => getAIReviewSourceType(),
  });
};

export const useCreateAIReviewJob = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  const queryClient = useQueryClient();
  return useMutation<
    ICreateAIReviewJobResponse,
    Error,
    ICreateAIReviewJobPayload
  >({
    mutationFn: createAIReviewJob,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: REVIEW_JOB_QUERY_KEY});
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useCreateProtocolIntelligence = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<any, Error, any>({
    mutationFn: createProtocolIntelligence,
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useUpdateProtocolIntelligenceDetails = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<
    IUpdateProtocolIntelligenceDetailsResponse,
    Error,
    IUpdateProtocolIntelligenceDetailsPayload
  >({
    mutationFn: updateProtocolIntelligenceDetails,
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useUpdateAIReviewJob = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  const queryClient = useQueryClient();

  return useMutation<
    IUpdateAIReviewJobResponse,
    Error,
    IUpdateAIReviewJobPayload
  >({
    mutationFn: updateAIReviewJob,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: REVIEW_JOB_QUERY_KEY});

      onSuccess?.();
    },
    onError: (err) => {
      onError?.(err);
    },
  });
};

export const useUpdateAIReviewJobName = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  const queryClient = useQueryClient();
  return useMutation<
    IAIReviewEditJobNameResponse,
    Error,
    IAIReviewEditJobNamePayload
  >({
    mutationFn: updateAIReviewJobName,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: REVIEW_JOB_QUERY_KEY});
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useApproveProtocolIntelligence = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  return useMutation<ProtocolIntelligenceApprovalResponse, Error, any>({
    mutationFn: approveProtocolIntelligence,
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useSubmitAIReviewJob = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  const queryClient = useQueryClient();
  return useMutation<
    ISubmitAIReviewJobResponse,
    Error,
    ISubmitAIReviewJobPayload
  >({
    mutationFn: submitAIReviewJob,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: REVIEW_JOB_QUERY_KEY});
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetAIReviewJobDetails = (
  payload: IGetAIReviewJobDetailsPayload,
  enabled: boolean
) => {
  return useQuery<IGetAIReviewJobDetailsResponse, Error>({
    queryKey: [...REVIEW_JOB_QUERY_KEY, payload],
    queryFn: () => getAIReviewJobDetails(payload),
    enabled,
  });
};

export const useExportAIReviewJobs = () => {
  return useMutation<Blob, Error, IAIReviewJobsExportRequest>({
    mutationFn: exportAIReviewJobs,
  });
};

export const useGetAIReviewJobDetailData = () => {
  return useMutation<
    IGetAIReviewJobDetailsResponse,
    Error,
    IExportAIReviewJobPdfPayload
  >({
    mutationFn: getAIReviewJobDetailData,
  });
};

export const useDeleteAIReviewJob = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  const queryClient = useQueryClient();
  return useMutation<
    IDeleteAIReviewJobResponse,
    Error,
    IDeleteAIReviewJobPayload
  >({
    mutationFn: deleteAIReviewJob,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: REVIEW_JOB_QUERY_KEY});
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useRetryAIReviewJob = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  const queryClient = useQueryClient();
  return useMutation<
    IRetryAIReviewJobResponse,
    Error,
    IRetryAIReviewJobPayload
  >({
    mutationFn: retryAIReviewJob,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: REVIEW_JOB_QUERY_KEY});
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useAddJobToGroup = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  const queryClient = useQueryClient();
  return useMutation<IAddJobToGroupResponse, Error, IAddJobToGroupPayload>({
    mutationFn: addJobToGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: REVIEW_JOB_QUERY_KEY});
      queryClient.invalidateQueries({queryKey: REVIEW_JOB_GROUPS_QUERY_KEY});
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useRemoveJobFromGroup = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  const queryClient = useQueryClient();
  return useMutation<
    IRemoveJobFromGroupResponse,
    Error,
    IRemoveJobFromGroupPayload
  >({
    mutationFn: removeJobFromGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: REVIEW_JOB_QUERY_KEY});
      queryClient.invalidateQueries({queryKey: REVIEW_JOB_GROUPS_QUERY_KEY});
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetAIReviewJobResult = (
  payload: IGetAIReviewJobResultPayload
) => {
  return useQuery<IGetAIReviewJobResultResponse, Error>({
    queryKey: [...REVIEW_JOB_QUERY_KEY, 'result', payload],
    queryFn: () => getAIReviewJobResult(payload),
  });
};

export const useGetAIReviewJobResultIssueCodeList = (
  jobId: number | string,
  payload: IAIReviewJobResultIssueCodeListPayload,
  type: 'Issue Coding' | 'Relevancy'
) => {
  return useQuery<IAIReviewJobResultIssueCodeListResponse, Error>({
    queryKey: [...REVIEW_JOB_QUERY_KEY, jobId, 'issue-code-list', payload],
    queryFn: () => getAIReviewJobResultIssueCodeList(jobId, payload),
    enabled: Boolean(jobId) && type === 'Issue Coding',
  });
};

export const useGetAIReviewJobResultRelevancyList = (
  jobId: number | string,
  payload: IAIReviewJobResultRelevancyListPayload,
  type: 'Issue Coding' | 'Relevancy'
) => {
  return useQuery<IAIReviewJobResultRelevancyListResponse, Error>({
    queryKey: [...REVIEW_JOB_QUERY_KEY, jobId, 'relevancy-list', payload],
    queryFn: () => getAIReviewJobResultRelevancyList(jobId, payload),
    enabled: Boolean(jobId) && type === 'Relevancy',
  });
};

export const useGetAIReviewDocErrorPanel = (
  payload: IGetAIReviewDocErrorPanelPayload
) => {
  return useQuery<IAIReviewDocErrorPanelResponse, Error>({
    queryKey: [...REVIEW_DOC_ERROR_PANEL_QUERY_KEY, 'result', payload],
    queryFn: () => getAIReviewDocErrorPanelList(payload),
  });
};

export const useGetJobList = (
  onSuccess?: (response: IGetJobListResponse) => void,
  onError?: (err: Error) => void
) => {
  return useMutation<IGetJobListResponse, Error, IGetJobListPayload>({
    mutationFn: (payload) => getJobList(payload),
    mutationKey: ['getJobList'],
    onSuccess: (response: IGetJobListResponse) => {
      onSuccess?.(response);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export const useGetGroundTruthEvaluation = (jobId: string) => {
  return useQuery<IGetGroundTruthEvaluationResponse, Error>({
    queryKey: ['getGroundTruthEvaluation'],
    queryFn: () => getGroundTruthEvaluation(jobId),
  });
};

export const useDeleteAIProtocolIntelligence = (
  onSuccess?: () => void,
  onError?: (err: Error) => void
) => {
  const queryClient = useQueryClient();
  return useMutation<
    IDeleteAIProtocolIntelligenceResponse,
    Error,
    IDeleteAIProtocolIntelligencePayload
  >({
    mutationFn: deleteAIProtocolIntelligence,
    onSuccess: async (_, variables) => {
      queryClient.removeQueries({
        queryKey: [...PROTOCOL_INTELLIGENCE_QUERY_KEY, variables.protocolId],
      });
      await queryClient.invalidateQueries({
        queryKey: PROTOCOL_INTELLIGENCE_QUERY_KEY,
        exact: true,
      });
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};
